import { Button, CircularProgress, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import LeftSide from '../components/LeftSide';
import Navbar from '../components/Navbar';
import TickerNews from '../components/TickerNews';
import { fetchSabaGames, launchSaba } from '../api/saba';
import '../styles/Messages.css';

/**
 * The games the player can launch: SABA's catalogue, read from our backend, each tile opening that one game
 * straight away (game_id on the launch call) instead of dropping the player on SABA's homepage.
 */
const useStyles = makeStyles((theme) => ({
  MainArea: {
    width: '100%',
    display: 'grid',
    placeItems: 'center',
    maxWidth: '1180px',
    margin: '86px auto auto !important',
    float: 'none !important',
  },
  MainArea2: {
    width: '80%',
    display: 'grid',
    placeItems: 'center',
    maxWidth: '1180px',
    margin: '10px auto auto !important',
    float: 'none !important',
  },
  flex: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '10px',
    maxHeight: '18px',
  },
  flex2: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  img: {
    height: '18px',
    width: '18px',
    marginRight: '0.5rem',
  },
  header: {
    color: '#18334C',
    fontSize: '14px',
    fontWeight: 'bold',
    margin: '0',
  },
  LeftPanel: {
    width: '20%',
  },
  RightPanel: {
    width: '80%',
    marginTop: '10px',
  },
  games: {
    width: '100%',
    padding: '0 8px',
  },
  card: {
    position: 'relative',
    cursor: 'pointer',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#ffffff',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
    '&:hover $overlay': {
      opacity: 1,
    },
  },
  logo: {
    display: 'block',
    width: '100%',
  },
  name: {
    display: 'block',
    padding: '6px 8px',
    color: '#18334C',
    fontSize: '13px',
    fontWeight: 'bold',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    opacity: 0,
    transition: 'opacity 0.2s',
  },
  overlayLocked: {
    opacity: 1,
  },
  play: {
    height: '32px',
    width: '32px',
  },
  message: {
    display: 'grid',
    placeItems: 'center',
    padding: '24px 0',
    color: '#323030',
    fontSize: '13px',
    textAlign: 'center',
  },
  [theme.breakpoints.down('sm')]: {
    MainArea2: {
      width: '100%',
    },
  },
}));

const Games = ({ User }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  // null means still loading; [] means the catalogue came back with nothing to show
  const [games, setGames] = useState(null);
  const [failed, setFailed] = useState(false);
  const [launching, setLaunching] = useState(null);
  // the left panel's bet slip reads and writes this, as on every other player page
  const [quote, setQuote] = useState(null);

  const loadGames = useCallback(() => {
    setFailed(false);
    setGames(null);
    fetchSabaGames()
      .then((list) => {
        setGames(list);
      })
      .catch((err) => {
        console.log(err);
        setFailed(true);
        setGames([]);
      });
  }, []);

  useEffect(() => {
    loadGames();
  }, [loadGames]);

  const handleLaunch = async (game) => {
    if (launching) return;

    // opened before the request so the browser keeps it: a tab opened after an await is blocked as a popup
    const newTab = window.open('about:blank', '_blank');
    setLaunching(game.id);

    try {
      const url = await launchSaba(game.id);
      if (url) {
        if (newTab) {
          newTab.location.href = url;
        } else {
          window.location.href = url;
        }
      } else {
        if (newTab) newTab.close();
        toast.error(t('Unable to launch. Your session may have expired.'), {
          position: 'top-right',
          autoClose: 5000,
        });
      }
    } catch (err) {
      if (newTab) newTab.close();
      console.log(err);
      toast.error(t('Failed to connect. Please try again.'), {
        position: 'top-right',
        autoClose: 5000,
      });
    } finally {
      setLaunching(null);
    }
  };

  return (
    <>
      <Navbar User={User.user} />
      <div className={classes.MainArea}>
        <TickerNews />
      </div>
      <div className={classes.MainArea2}>
        <Grid container spacing={2}>
          <Grid item className={classes.LeftPanel}>
            <LeftSide User={User} quote={quote} setQuote={setQuote} />
          </Grid>
          <Grid item className={classes.RightPanel}>
            <div className="Messages">
              <div className={classes.flex}>
                <div className={classes.flex2}>
                  <img
                    src={require('../img/Icons/game.svg')}
                    alt=""
                    className={classes.img}
                  />
                  <p className={classes.header}>
                    <span className={`lang-${localStorage.getItem('language')}`}>
                      {t('Games')}
                    </span>
                  </p>
                </div>
              </div>

              {failed ? (
                <div className={classes.message}>
                  <p>{t('Unable to load games. Please try again.')}</p>
                  <Button variant="contained" color="primary" onClick={loadGames}>
                    {t('Retry')}
                  </Button>
                </div>
              ) : games === null ? (
                <div className={classes.message}>
                  <CircularProgress size={28} />
                  <p>{t('Loading games...')}</p>
                </div>
              ) : games.length === 0 ? (
                <div className={classes.message}>
                  <p>{t('No games available.')}</p>
                </div>
              ) : (
                <div className={classes.games}>
                  <Grid container spacing={2}>
                    {games.map((game) => (
                      <Grid item xs={6} sm={4} md={3} key={game.id || game.game}>
                        <div
                          className={classes.card}
                          onClick={() => handleLaunch(game)}
                          title={game.name}
                        >
                          <img
                            className={classes.logo}
                            src={game.urlLogoLarge || game.urlLogo}
                            alt={game.name}
                          />
                          <span className={classes.name}>{game.name}</span>
                          <div
                            className={`${classes.overlay} ${
                              launching === game.id ? classes.overlayLocked : ''
                            }`}
                          >
                            {launching === game.id ? (
                              <CircularProgress size={28} />
                            ) : (
                              <img
                                src={require('../img/Icons/game.svg')}
                                alt=""
                                className={classes.play}
                              />
                            )}
                          </div>
                        </div>
                      </Grid>
                    ))}
                  </Grid>
                </div>
              )}
            </div>
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default Games;
